/**
 * WHAT: AI-powered natural language query interface for database access governance data.
 * WHY: Allows non-technical users to query DAMA's database access data using plain English,
 *      lowering the barrier for auditors and managers who need answers about user access,
 *      compliance status, and database inventory without writing SQL.
 */

import React, { useState, useEffect, useRef } from 'react';
import API_CONFIG from '../config/api.config';
import './AIQueryAssistant.css';

/**
 * WHAT: Chat-style conversational interface that translates natural language queries
 *       into SQL, executes them, and displays results in a tabular format.
 * WHY: Empowers auditors and managers to self-serve access governance questions
 *      without DBA assistance, accelerating compliance investigations.
 */
const AIQueryAssistant = () => {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [queryHistory, setQueryHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [agentHealth, setAgentHealth] = useState(null);
  const messagesEndRef = useRef(null);

  // Sample queries for user guidance
  const sampleQueries = [
    "Show me all MySQL databases with SOX compliance",
    "Who has DBA access on production databases?",
    "List all databases that failed the last attestation",
    "What applications use the customer_db database?",
    "Show me all databases owned by john.doe@company.com"
  ];

  useEffect(() => {
    checkAgentHealth();
    loadQueryHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkAgentHealth = async () => {
    try {
      const response = await fetch(API_CONFIG.getEndpoint('/api/databases/agents/health/'));
      const data = await response.json();
      setAgentHealth(data);
    } catch (error) {
      console.error('Health check failed:', error);
      setAgentHealth({ success: false, status: 'unhealthy' });
    }
  };

  const loadQueryHistory = async () => {
    try {
      const response = await fetch(API_CONFIG.getEndpoint('/api/databases/agents/query-history/?limit=10'));
      const data = await response.json();
      if (data.success) {
        setQueryHistory(data.history);
      }
    } catch (error) {
      console.error('Failed to load query history:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    // Add user message to conversation
    const userMessage = {
      type: 'user',
      content: query,
      timestamp: new Date()
    };
    setConversationHistory(prev => [...prev, userMessage]);

    const currentQuery = query;
    setQuery('');
    setIsLoading(true);

    try {
      const response = await fetch(API_CONFIG.getEndpoint('/api/databases/agents/nl-query/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: currentQuery })
      });

      const data = await response.json();

      // Add agent response to conversation
      const agentMessage = {
        type: 'agent',
        content: data,
        timestamp: new Date()
      };
      setConversationHistory(prev => [...prev, agentMessage]);

      // Reload query history
      loadQueryHistory();

    } catch (error) {
      const errorMessage = {
        type: 'error',
        content: { error: `Failed to execute query: ${error.message}` },
        timestamp: new Date()
      };
      setConversationHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSampleQuery = (sampleQuery) => {
    setQuery(sampleQuery);
  };

  const clearConversation = () => {
    setConversationHistory([]);
  };

  const renderMessage = (message, index) => {
    if (message.type === 'user') {
      return (
        <div key={index} className="message user-message">
          <div className="message-header">
            <span className="message-icon">👤</span>
            <span className="message-label">You</span>
            <span className="message-time">{message.timestamp.toLocaleTimeString()}</span>
          </div>
          <div className="message-content">
            {message.content}
          </div>
        </div>
      );
    }

    if (message.type === 'agent') {
      const { success, query, sql, results, results_count, execution_time_ms, intent, explanation, error } = message.content;

      return (
        <div key={index} className="message agent-message">
          <div className="message-header">
            <span className="message-icon">🤖</span>
            <span className="message-label">AI Assistant</span>
            <span className="message-time">{message.timestamp.toLocaleTimeString()}</span>
          </div>

          {success ? (
            <div className="message-content">
              {explanation && (
                <div className="explanation">
                  <strong>Analysis:</strong> {explanation}
                </div>
              )}

              <div className="query-metadata">
                <span className="metadata-item">
                  <span className="metadata-label">Intent:</span>
                  <span className="metadata-value intent-badge">{intent}</span>
                </span>
                <span className="metadata-item">
                  <span className="metadata-label">Results:</span>
                  <span className="metadata-value">{results_count} rows</span>
                </span>
                <span className="metadata-item">
                  <span className="metadata-label">Time:</span>
                  <span className="metadata-value">{execution_time_ms}ms</span>
                </span>
              </div>

              <details className="sql-details">
                <summary>📝 View Generated SQL</summary>
                <pre className="sql-code">{sql}</pre>
              </details>

              {results && results.length > 0 ? (
                <div className="results-container">
                  <div className="results-header">
                    <strong>Results ({results_count}):</strong>
                  </div>
                  <div className="results-table-wrapper">
                    <table className="results-table">
                      <thead>
                        <tr>
                          {Object.keys(results[0]).map((key, i) => (
                            <th key={i}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {Object.values(row).map((value, colIndex) => (
                              <td key={colIndex}>{value !== null ? String(value) : 'null'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="no-results">
                  ℹ️ Query executed successfully but returned no results.
                </div>
              )}
            </div>
          ) : (
            <div className="message-content error-content">
              <div className="error-icon">⚠️</div>
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
              {sql && (
                <details className="sql-details">
                  <summary>View SQL</summary>
                  <pre className="sql-code">{sql}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      );
    }

    if (message.type === 'error') {
      return (
        <div key={index} className="message error-message">
          <div className="message-header">
            <span className="message-icon">⚠️</span>
            <span className="message-label">Error</span>
            <span className="message-time">{message.timestamp.toLocaleTimeString()}</span>
          </div>
          <div className="message-content">
            {message.content.error}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="ai-query-assistant">
      <div className="assistant-header">
        <h1>
          <span className="header-icon">🤖</span>
          AI Query Assistant
        </h1>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={() => setShowHistory(!showHistory)}
          >
            📋 {showHistory ? 'Hide' : 'Show'} History
          </button>
          <button className="btn-secondary" onClick={clearConversation}>
            🗑️ Clear Chat
          </button>
          {agentHealth && (
            <div className={`health-indicator ${agentHealth.status}`}>
              <span className={`status-dot ${agentHealth.status}`}></span>
              {agentHealth.status === 'healthy' ? '✓ Ready' : '⚠️ Degraded'}
            </div>
          )}
        </div>
      </div>

      {agentHealth && !agentHealth.llm_configured && (
        <div className="alert alert-warning">
          <strong>⚠️ Configuration Required:</strong> ANTHROPIC_API_KEY is not configured.
          Please set it in the backend/.env file to enable AI-powered queries.
        </div>
      )}

      <div className="assistant-container">
        {showHistory && (
          <div className="history-panel">
            <h3>Recent Queries</h3>
            <div className="history-list">
              {queryHistory.length > 0 ? (
                queryHistory.map((item, index) => (
                  <div
                    key={index}
                    className={`history-item ${item.status === 'ERROR' ? 'error' : 'success'}`}
                    onClick={() => handleSampleQuery(item.query)}
                  >
                    <div className="history-query">{item.query}</div>
                    <div className="history-meta">
                      <span className={`history-status ${item.status.toLowerCase()}`}>
                        {item.status === 'SUCCESS' ? '✓' : '✗'}
                      </span>
                      <span className="history-intent">{item.intent}</span>
                      {item.results_count !== null && (
                        <span className="history-count">{item.results_count} results</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-history">No query history yet</div>
              )}
            </div>
          </div>
        )}

        <div className="conversation-panel">
          <div className="conversation-area">
            {conversationHistory.length === 0 ? (
              <div className="welcome-screen">
                <div className="welcome-icon">🤖</div>
                <h2>Welcome to AI Query Assistant</h2>
                <p>Ask me anything about your database access governance in plain English.</p>

                <div className="sample-queries">
                  <h3>Try these sample queries:</h3>
                  {sampleQueries.map((sample, index) => (
                    <div
                      key={index}
                      className="sample-query-card"
                      onClick={() => handleSampleQuery(sample)}
                    >
                      <span className="sample-icon">💡</span>
                      <span className="sample-text">{sample}</span>
                    </div>
                  ))}
                </div>

                <div className="capabilities">
                  <h3>What I can help with:</h3>
                  <ul>
                    <li>🔍 Find databases by type, compliance framework, or environment</li>
                    <li>👥 Identify users with specific access or privileges</li>
                    <li>📊 Query campaign and attestation status</li>
                    <li>🏢 Explore application-database relationships</li>
                    <li>🎯 Check compliance framework assignments</li>
                  </ul>
                </div>
              </div>
            ) : (
              <>
                {conversationHistory.map((message, index) => renderMessage(message, index))}
                {isLoading && (
                  <div className="message agent-message loading">
                    <div className="message-header">
                      <span className="message-icon">🤖</span>
                      <span className="message-label">AI Assistant</span>
                    </div>
                    <div className="message-content">
                      <div className="loading-indicator">
                        <div className="loading-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <span className="loading-text">Analyzing query and generating SQL...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          <form className="query-input-container" onSubmit={handleSubmit}>
            <input
              type="text"
              className="query-input"
              placeholder="Ask me anything about your databases... (e.g., 'Show me all MySQL databases')"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={isLoading}
            />
            <button
              type="submit"
              className="btn-submit"
              disabled={isLoading || !query.trim()}
            >
              {isLoading ? '⏳' : '🚀'} {isLoading ? 'Processing...' : 'Send'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AIQueryAssistant;
